import React, { useEffect, useRef, useState } from 'react';
import { applyLegalBoardGencat, downloadLegalBoardCertificateDocument, openLegalBoardStripe, refreshLegalBoardStripe } from './api';
import { LEGAL_BOARD_RETURN_PATH, OFFICIAL_LINKS } from './constants';
import LegalBoardCertificateDocument from './LegalBoardCertificateDocument';
import LegalBoardHistory from './LegalBoardHistory';
import { Disclosure, ExternalLink, StateCorrection, StatusPill } from './LegalBoardPrimitives';
import {
    STEP_KEYS,
    buildStripeTasks,
    changeTypeLabel,
    computeStepStates,
    describeDaysLeft,
    describeStripeRequirement,
    formatDateCa,
    formatDateTimeCa,
    memberFullName,
    stripeDeadline,
    stripeErrorLines,
    stripeStateUnknown,
    newRequestId,
    summarizeOverview,
} from './legalBoardUi';

/** Explain the Generalitat state without confusing "marked as submitted" with "registered". */
export const describeGencat = (term) => {
    switch (term?.gencatStatus) {
        case 'registered':
            if (term.gencatRegisteredAt) return `La Generalitat ho ha inscrit el ${formatDateCa(term.gencatRegisteredAt)}.`;
            return term.changeType === 'initial'
                ? 'Has declarat que aquesta junta ja consta inscrita al Registre.'
                : 'Marcada com a inscrita.';
        case 'submitted':
            return `Ho has marcat com a presentat${term.gencatSubmittedAt ? ` el ${formatDateCa(term.gencatSubmittedAt)}` : ''}${term.gencatReference ? ` (referència ${term.gencatReference})` : ''}. La Generalitat encara no ho ha inscrit; pot trigar fins a tres mesos.`;
        case 'correction_required':
            return 'La Generalitat demana corregir la documentació. Quan la tornis a presentar, marca-ho aquí.';
        default:
            return 'Cal comunicar la junta al Registre d\'Associacions. Marca-ho com a presentat només quan hagis enviat el tràmit al portal oficial.';
    }
};

const STEP_ORDER = [STEP_KEYS.BOARD, STEP_KEYS.GENCAT, STEP_KEYS.STRIPE];
const STEP_TITLES = {
    [STEP_KEYS.BOARD]: 'Junta a Aleta',
    [STEP_KEYS.GENCAT]: 'Comunicació a la Generalitat',
    [STEP_KEYS.STRIPE]: 'Directius a Stripe',
};
const STATE_LABELS = { done: 'Fet', active: 'Pas actual', pending: 'Pendent', na: 'No cal' };

const GENCAT_CORRECTIONS = {
    registered: {
        label: 'Desmarcar com a inscrita',
        description: 'Tornarà a constar com a presentada a Aleta. No anul·la la inscripció oficial.',
    },
    submitted: {
        label: 'Desmarcar com a presentada',
        description: 'Tornarà a quedar pendent de presentar a Aleta. No retira el tràmit enviat a la Generalitat.',
    },
    correction_required: {
        label: 'Treure la marca de correcció pendent',
        description: 'Tornarà a constar com a presentada a Aleta. No modifica el requeriment de la Generalitat.',
    },
    preparing: {
        label: 'Desmarcar el tràmit com a iniciat',
        description: 'Tornarà a quedar pendent de presentar a Aleta.',
    },
};

/**
 * Open by default: only the step to act on, so it is clear where to act; the
 * done ones fold into their one-line header. With nothing to do, the done
 * steps stay in view (the board, the registration, the certificate).
 */
const defaultOpen = (steps) => {
    const hasActive = Object.values(steps).some((step) => step.state === 'active');
    return Object.fromEntries(
        Object.entries(steps).map(([key, step]) => [key, hasActive ? step.state === 'active' : step.state === 'done'])
    );
};

function DateForm({ id, label, withReference = false, submitLabel, busy, onSubmit, onCancel }) {
    const [date, setDate] = useState('');
    const [reference, setReference] = useState('');
    return (
        <div className="legal-board-inline-form">
            <div className="legal-board-field">
                <label htmlFor={`${id}-date`}>{label}</label>
                <input id={`${id}-date`} type="date" value={date} onChange={(event) => setDate(event.target.value)} disabled={busy} />
            </div>
            {withReference ? (
                <div className="legal-board-field">
                    <label htmlFor={`${id}-reference`}>Número de referència (opcional)</label>
                    <input id={`${id}-reference`} type="text" value={reference} onChange={(event) => setReference(event.target.value)} disabled={busy} />
                </div>
            ) : null}
            <div className="legal-board-inline-form-actions">
                <button type="button" className="legal-board-button primary" onClick={() => onSubmit({ date, reference })} disabled={busy || !date}>{submitLabel}</button>
                <button type="button" className="legal-board-button link" onClick={onCancel} disabled={busy}>Cancel·lar</button>
            </div>
        </div>
    );
}

function NoteForm({ id, label, submitLabel, busy, onSubmit, onCancel }) {
    const [note, setNote] = useState('');
    return (
        <div className="legal-board-inline-form">
            <div className="legal-board-field">
                <label htmlFor={`${id}-note`}>{label}</label>
                <textarea id={`${id}-note`} rows={2} value={note} onChange={(event) => setNote(event.target.value)} disabled={busy} />
            </div>
            <div className="legal-board-inline-form-actions">
                <button type="button" className="legal-board-button primary" onClick={() => onSubmit({ note })} disabled={busy}>{submitLabel}</button>
                <button type="button" className="legal-board-button link" onClick={onCancel} disabled={busy}>Cancel·lar</button>
            </div>
        </div>
    );
}

/**
 * The whole tab in one card, read top to bottom in the order things happen:
 * header (status in one line) → 1 Junta a Aleta → 2 Generalitat → 3 Stripe →
 * history. Each step is an accordion whose header already tells its state;
 * only the step to act on is open by default (see `defaultOpen`).
 */
/**
 * Stripe directors whose name is a misspelling of a board member: the person
 * is already declared, so the name is corrected rather than added again.
 */
function NameCorrections({ mismatches }) {
    return (mismatches || []).map((mismatch) => (
        <p key={mismatch.director} className="legal-board-stripe-task-hint">
            Corregeix a Stripe el nom de <strong>{mismatch.director}</strong> (a la junta, {mismatch.member}) perquè sigui igual que al certificat. No cal treure’l ni tornar-lo a afegir.
        </p>
    ));
}

function LegalBoardSteps({
    overview,
    userId,
    canManage,
    busy: parentBusy = false,
    openRequest = null,
    onReload,
    onChangeTab,
    onNotice,
    onConfigure,
    onContinueDraft,
    onDiscardDraft,
    onCorrect,
    onPrepareChange,
    onHowToKnow,
}) {
    const current = overview?.current || null;
    const draft = overview?.draft || null;
    const stripe = overview?.stripe || null;
    const history = overview?.history || [];
    const summary = summarizeOverview(overview);
    const { activeKey, steps } = computeStepStates(overview);

    // Which accordions are open. `null` means "the defaults" (see `defaultOpen`);
    // the defaults are recomputed whenever the step to act on changes.
    const [openKeys, setOpenKeys] = useState(null);
    const stateSignature = STEP_ORDER.map((key) => steps[key].state).join('|');
    const lastSignature = useRef(stateSignature);
    useEffect(() => {
        if (lastSignature.current !== stateSignature) {
            lastSignature.current = stateSignature;
            setOpenKeys(null);
        }
    }, [stateSignature]);
    const openStep = (key) => setOpenKeys((value) => ({ ...(value || defaultOpen(steps)), [key]: true }));
    const handledRequest = useRef(null);
    useEffect(() => {
        if (!openRequest || openRequest.id === handledRequest.current) return;
        handledRequest.current = openRequest.id;
        openStep(openRequest.key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openRequest]);
    const isOpen = (key) => (openKeys ? Boolean(openKeys[key]) : Boolean(defaultOpen(steps)[key]));
    const toggle = (key) => setOpenKeys((value) => {
        const base = value || defaultOpen(steps);
        return { ...base, [key]: !base[key] };
    });

    const [busyAction, setBusyAction] = useState(false);
    const busy = busyAction || parentBusy;
    const [error, setError] = useState(null);
    const [activeForm, setActiveForm] = useState(null); // 'submit' | 'register' | 'correction' | null
    // The how-to is open while nothing has been submitted yet and folds once the
    // process moves on (it stays available for reference).
    const gencatStatus = current?.gencatStatus || null;
    const howToOpenByDefault = gencatStatus === 'not_started' || gencatStatus === 'preparing';
    const [showHowTo, setShowHowTo] = useState(howToOpenByDefault);
    const lastGencatStatus = useRef(gencatStatus);
    useEffect(() => {
        if (lastGencatStatus.current !== gencatStatus) {
            lastGencatStatus.current = gencatStatus;
            setShowHowTo(howToOpenByDefault);
        }
    }, [gencatStatus, howToOpenByDefault]);
    const [showStripeDetails, setShowStripeDetails] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const requestIds = useRef({});
    const stripeOpening = useRef(false);

    const editable = canManage && Boolean(current);
    const connected = Boolean(stripe?.connected);
    // A board the colla presented and the Generalitat then registered: Stripe may
    // still read the previous one. An initial board that already was registered is not new.
    const newBoardRegisteredAt = current && current.gencatStatus === 'registered' && (current.changeType !== 'initial' || current.gencatSubmittedAt)
        ? current.gencatRegisteredAt || null
        : null;
    const stripeTasks = current && connected
        ? buildStripeTasks(stripe, { certificateDocument: current.certificateDocument, newBoardRegisteredAt })
        : [];
    // Without a stored PDF, the certificate is saved where Stripe asks for it, not in step 2.
    const certificateInStripe = editable && !current.certificateDocument && stripeTasks.some((task) => task.key === 'certificate');

    const run = async (action) => {
        if (busy) return;
        setBusyAction(true);
        setError(null);
        try {
            await action();
        } catch (actionError) {
            setError(actionError.message || 'No s\'ha pogut completar l\'acció.');
        } finally {
            setBusyAction(false);
        }
    };

    const withRequestId = (key) => {
        if (!requestIds.current[key]) requestIds.current[key] = newRequestId();
        return requestIds.current[key];
    };

    const handleGencat = (action, payload) => run(async () => {
        await applyLegalBoardGencat({ userId, termId: current.id, action, requestId: withRequestId(`gencat-${action}`), ...payload });
        requestIds.current[`gencat-${action}`] = null;
        setActiveForm(null);
        await onReload();
    });

    const handleOpenStripe = async () => {
        if (stripeOpening.current || busy) return;
        stripeOpening.current = true;
        setBusyAction(true);
        setError(null);
        try {
            const response = await openLegalBoardStripe({ userId, termId: current.id, returnPath: LEGAL_BOARD_RETURN_PATH });
            if (response?.url) {
                window.location.assign(response.url);
                return;
            }
            setError('Stripe no ha retornat cap enllaç. Torna-ho a provar.');
        } catch (openError) {
            setError(openError.message || 'No s\'ha pogut obrir Stripe.');
        } finally {
            stripeOpening.current = false;
            setBusyAction(false);
        }
    };

    const handleDownloadCertificate = () => run(() => downloadLegalBoardCertificateDocument({ userId, termId: current.id, name: current.certificateDocument?.name }));

    const handleRefreshStripe = () => run(async () => {
        const result = await refreshLegalBoardStripe({ userId, termId: current.id });
        await onReload();
        if (stripeStateUnknown(result.stripe)) {
            if (typeof onNotice === 'function') onNotice({ tone: 'warning', text: 'No s’ha pogut comprovar tot l’estat de Stripe. Torna-ho a provar.' });
            return;
        }
        if (typeof onNotice === 'function') onNotice({ tone: 'info', text: 'Estat de Stripe actualitzat amb la informació real del compte.' });
    });

    // -------------------------------------------------------------------
    // Step 1 · Junta a Aleta
    // -------------------------------------------------------------------

    const renderBoardBody = () => {
        if (draft) {
            return (
                <>
                    <p>
                        Estàs preparant una {changeTypeLabel(draft.changeType).toLowerCase()}
                        {draft.updatedAt ? `, desada el ${formatDateTimeCa(draft.updatedAt)}` : ''}.
                    </p>
                    {canManage ? (
                        <div className="legal-board-actions">
                            <button type="button" className="legal-board-button primary" onClick={onContinueDraft} disabled={busy}>Continuar el canvi de junta</button>
                            <button type="button" className="legal-board-button link" onClick={onDiscardDraft} disabled={busy}>Descartar l&apos;esborrany</button>
                        </div>
                    ) : (
                        <p className="legal-board-muted legal-board-small">Només un cap de junta pot continuar el canvi.</p>
                    )}
                    {current ? (
                        <p className="legal-board-muted legal-board-small">
                            Junta vigent mentre no confirmis la nova: {(current.members || []).map(memberFullName).join(' · ')}.
                        </p>
                    ) : null}
                </>
            );
        }
        if (!current) {
            return (
                <>
                    <p>Registra qui forma la junta legal que consta (o ha de constar) al Registre d&apos;Associacions.</p>
                    {canManage ? (
                        <div className="legal-board-actions">
                            <button type="button" className="legal-board-button primary" onClick={onConfigure} disabled={busy}>Registrar la junta legal</button>
                        </div>
                    ) : (
                        <p className="legal-board-muted legal-board-small">Només un cap de junta pot registrar-la.</p>
                    )}
                    <p className="legal-board-muted legal-board-small">
                        Pots veure quina junta hi consta a la <ExternalLink link={OFFICIAL_LINKS.gencatEntitiesGuide}>Guia d&apos;entitats</ExternalLink>.
                        {' '}Si necessites un document oficial, <button type="button" className="legal-board-inline-action" onClick={onHowToKnow} disabled={busy}>mira com demanar-lo</button>.
                    </p>
                </>
            );
        }
        return (
            <>
                <ul className="legal-board-member-list" aria-label="Membres de la junta legal">
                    {(current.members || []).map((member) => (
                        <li key={member.id || `${member.legalFirstName}-${member.legalLastName}`}>
                            <span className="legal-board-member-name">{memberFullName(member)}</span>
                            <span className="legal-board-member-role">{member.roleLabel || 'Sense càrrec'}</span>
                        </li>
                    ))}
                </ul>
                <div className="legal-board-board-meta">
                    <p className="legal-board-muted legal-board-small">
                        Vigent des del {formatDateCa(current.mandateStart) || '—'}
                        {current.mandateEnd ? ` fins al ${formatDateCa(current.mandateEnd)}` : ''}
                        {' · '}Pròxima revisió {formatDateCa(current.nextReviewDate) || 'sense data'}
                    </p>
                    {canManage ? (
                        <button type="button" className="legal-board-button secondary" onClick={onCorrect} disabled={busy}>
                            <i className="fas fa-pen" aria-hidden="true"></i> Corregir una dada
                        </button>
                    ) : null}
                </div>
                {canManage && !summary.renew ? (
                    <div className="legal-board-actions">
                        <button type="button" className="legal-board-button" onClick={onPrepareChange} disabled={busy}>Preparar un canvi de junta</button>
                    </div>
                ) : null}
            </>
        );
    };

    // -------------------------------------------------------------------
    // Step 2 · Generalitat
    // -------------------------------------------------------------------

    const renderGencatActions = () => {
        if (!editable) return null;
        const status = current.gencatStatus;
        if (status === 'registered') return null;

        if (activeForm === 'submit') {
            return (
                <DateForm
                    id="legal-board-gencat-submit"
                    label="Data de presentació"
                    withReference
                    submitLabel="Desar la presentació"
                    busy={busy}
                    onSubmit={({ date, reference }) => handleGencat('submit', { date, reference })}
                    onCancel={() => setActiveForm(null)}
                />
            );
        }
        if (activeForm === 'register') {
            return (
                <DateForm
                    id="legal-board-gencat-register"
                    label="Data d'inscripció (segons la resolució)"
                    submitLabel="Desar la inscripció"
                    busy={busy}
                    onSubmit={({ date }) => handleGencat('register', { date })}
                    onCancel={() => setActiveForm(null)}
                />
            );
        }
        if (activeForm === 'correction') {
            return (
                <NoteForm
                    id="legal-board-gencat-correction"
                    label="Què demana corregir la Generalitat?"
                    submitLabel="Desar el requeriment"
                    busy={busy}
                    onSubmit={({ note }) => handleGencat('correction', { note })}
                    onCancel={() => setActiveForm(null)}
                />
            );
        }

        if (status === 'submitted') {
            return (
                <div className="legal-board-actions">
                    <button type="button" className="legal-board-button primary" onClick={() => setActiveForm('register')} disabled={busy}>La Generalitat ho ha inscrit</button>
                    <button type="button" className="legal-board-button link" onClick={() => setActiveForm('correction')} disabled={busy}>Cal corregir documentació</button>
                </div>
            );
        }
        return (
            <div className="legal-board-actions">
                <button type="button" className="legal-board-button primary" onClick={() => setActiveForm('submit')} disabled={busy}>
                    {status === 'correction_required' ? 'Tornar a marcar com a presentada' : 'Marcar com a presentada'}
                </button>
                <button type="button" className="legal-board-button link" onClick={() => setActiveForm('register')} disabled={busy}>Ja està inscrita</button>
            </div>
        );
    };

    const renderGencatHowTo = () => {
        const isTotal = current.changeType === 'total' || current.changeType === 'reelection';
        const isInitial = current.changeType === 'initial';
        const registered = current.gencatStatus === 'registered';
        let intro;
        if (isInitial && registered) {
            intro = 'Aquesta junta ja constava inscrita al Registre, així que no ha calgut cap tràmit. Quan la junta canviï, el tràmit serà aquest:';
        } else if (isInitial) {
            intro = 'Si aquesta junta ja consta inscrita al Registre, marca «Ja està inscrita». Si no hi consta, cal comunicar-la igual que una renovació:';
        } else {
            intro = `Correspon una ${isTotal ? 'renovació total (ha acabat el mandat i s\'ha escollit una junta nova)' : 'renovació parcial (el mandat continua però canvien persones o càrrecs)'}. És obligatori comunicar-la.`;
        }
        return (
            <Disclosure id="legal-board-gencat-howto" summary="Com es fa el tràmit" open={showHowTo} onToggle={() => setShowHowTo((value) => !value)}>
                <p>{intro}</p>
                <ol className="legal-board-list legal-board-procedure">
                    <li>Omple i signa el <ExternalLink link={OFFICIAL_LINKS.gencatRenewalForm} /></li>
                    <li>Prepara nom, cognoms, DNI/NIE i càrrec de cada membre. Aleta no guarda els DNI.</li>
                    <li>Presenta-ho al <ExternalLink link={OFFICIAL_LINKS.gencatBoardRegistration} /> amb certificat digital. <strong>La inscripció del canvi de junta és gratuïta.</strong> Les juntes anteriors han d&apos;estar inscrites.</li>
                    <li>Espera la resolució (fins a tres mesos). Quan la junta nova aparegui a la <ExternalLink link={OFFICIAL_LINKS.gencatEntitiesGuide}>Guia d&apos;entitats</ExternalLink>, marca aquí la inscripció.</li>
                    <li>Si necessites un document oficial de la junta inscrita (per exemple per a Stripe o el banc), demana la <ExternalLink link={OFFICIAL_LINKS.gencatRegistryCertificate}>certificació de dades registrals</ExternalLink> (tràmit telemàtic a part, amb taxa; tria la modalitat «Inscripció i òrgan de govern») i guarda el PDF aquí.</li>
                </ol>
            </Disclosure>
        );
    };

    const renderGencatBody = () => {
        if (!current) {
            return <p className="legal-board-muted">Quan hagis registrat la junta a Aleta, aquí en seguiràs la comunicació al Registre d&apos;Associacions.</p>;
        }
        const correction = editable ? GENCAT_CORRECTIONS[current.gencatStatus] : null;
        return (
            <>
                {draft ? <p className="legal-board-muted legal-board-small">Es tornarà a fer amb la junta nova quan la confirmis.</p> : null}
                <p>
                    {describeGencat(current)}
                    {current.gencatStatus === 'registered' ? (
                        <>
                            {' '}Es pot consultar a la <ExternalLink link={OFFICIAL_LINKS.gencatEntitiesGuide}>Guia d&apos;entitats</ExternalLink>.
                        </>
                    ) : (
                        <>
                            {' '}Pots comprovar si ja hi consta a la <ExternalLink link={OFFICIAL_LINKS.gencatEntitiesGuide}>Guia d&apos;entitats</ExternalLink>.
                        </>
                    )}
                </p>
                {renderGencatActions()}
                {correction ? <StateCorrection id="legal-board-gencat-correction-action" {...correction} onClick={() => handleGencat('revert')} disabled={busy} /> : null}
                {certificateInStripe ? null : <LegalBoardCertificateDocument key={current.id} term={current} userId={userId} canManage={canManage} disabled={busy} onReload={onReload} onHowToKnow={onHowToKnow} />}
                {renderGencatHowTo()}
            </>
        );
    };

    // -------------------------------------------------------------------
    // Step 3 · Stripe
    // -------------------------------------------------------------------

    const renderStripeBody = () => {
        if (!current) {
            return <p className="legal-board-muted">Quan la junta estigui registrada, aquí veuràs si els directius declarats a Stripe hi coincideixen.</p>;
        }
        if (!connected) {
            return (
                <>
                    <p>Aquesta colla no té cap compte de Stripe Connect: aquest pas no cal.</p>
                    {typeof onChangeTab === 'function' ? (
                        <button type="button" className="legal-board-button link" onClick={() => onChangeTab('stripe')}>Anar a Pagaments</button>
                    ) : null}
                </>
            );
        }

        const requirements = Array.from(new Set([...(stripe.personRequirements || []), ...(stripe.otherRequirements || [])].map(describeStripeRequirement).filter(Boolean)));
        const mismatches = stripe.directorsMatch?.nameMismatches || [];
        const misspelt = new Set(mismatches.map((mismatch) => mismatch.member));
        const unmatched = (stripe.directorsMatch?.unmatchedMembers || []).filter((name) => !misspelt.has(name));
        const extra = stripe.directorsMatch?.extraDirectors || [];
        const tasks = stripeTasks;
        const deadline = stripeDeadline(stripe, overview?.today);
        const actionable = tasks.length > 0;

        const pill = (enabled) => (
            <span className={`legal-board-pill legal-board-pill-${enabled === true ? 'success' : enabled === false ? 'urgent' : 'neutral'}`}>
                {enabled === true ? 'Actius' : enabled === false ? 'Aturats' : 'No disponible'}
            </span>
        );
        const statusRows = (
            <dl className="legal-board-rows compact">
                <div className="legal-board-row">
                    <dt>Cobraments</dt>
                    <dd>{pill(stripe.chargesEnabled)}</dd>
                </div>
                <div className="legal-board-row">
                    <dt>Payouts</dt>
                    <dd>{pill(stripe.payoutsEnabled)}</dd>
                </div>
                {deadline ? (
                    <div className="legal-board-row">
                        <dt>Data límit</dt>
                        <dd>{deadline.short}{deadline.daysLeft !== null && deadline.daysLeft >= 0 ? ` · ${describeDaysLeft(deadline.daysLeft)}` : ''}</dd>
                    </div>
                ) : null}
            </dl>
        );
        const directorsDiff = unmatched.length > 0 || extra.length > 0 || mismatches.length > 0 ? (
            <>
                {unmatched.length > 0 ? <p>No consten com a directius a Stripe: <strong>{unmatched.join(', ')}</strong>.</p> : null}
                {mismatches.map((mismatch) => (
                    <p key={mismatch.director}>A Stripe consta <strong>{mismatch.director}</strong> i a la junta, <strong>{mismatch.member}</strong>.</p>
                ))}
                {extra.length > 0 ? <p>A Stripe hi consten directius que no són a la junta: <strong>{extra.join(', ')}</strong>.</p> : null}
            </>
        ) : null;

        // The date already sits in the step header; the lead only says why.
        let lead = 'Stripe demana completar això:';
        if (stripe.status === 'restricted') lead = 'Per reactivar el compte, completa això a Stripe:';
        else if (stripe.restrictedSoon && deadline) lead = 'Per mantenir els cobraments actius, completa això a Stripe:';

        return (
            <>
                {draft ? <p className="legal-board-muted legal-board-small">Es tornarà a revisar amb la junta nova quan la confirmis.</p> : null}
                {stripeStateUnknown(stripe) ? <p className="legal-board-alert warning" role="status">No s’ha pogut comprovar tot l’estat de Stripe. Torna-ho a consultar per veure l’estat actual.</p> : null}
                {stripe.accountInvalid ? <p className="legal-board-alert warning">No es pot accedir al compte de Stripe. Revisa la connexió des de Pagaments.</p> : null}

                {actionable ? (
                    <>
                        <p className="legal-board-stripe-lead">{lead}</p>
                        <ol className="legal-board-stripe-tasks" aria-label="Passos per completar la verificació a Stripe">
                            {tasks.map((task, index) => renderStripeTask(task, index + 1))}
                        </ol>
                    </>
                ) : (
                    <>
                        {statusRows}
                        {directorsDiff}
                        {stripe.status === 'submitted' ? <p className="legal-board-muted legal-board-small">Stripe està revisant les dades enviades.</p> : null}
                        {stripe.status !== 'submitted' && !stripeStateUnknown(stripe) && !stripe.accountInvalid ? <p className="legal-board-muted legal-board-small">Stripe no demana dades noves ara mateix.</p> : null}
                    </>
                )}

                <div className="legal-board-actions">
                    {editable ? (
                        <button
                            type="button"
                            className="legal-board-button primary"
                            onClick={handleOpenStripe}
                            disabled={busy}
                            aria-busy={busy}
                        >
                            {actionable ? 'Completar-ho a Stripe' : 'Obrir Stripe per actualitzar la junta'}
                        </button>
                    ) : null}
                    <button type="button" className="legal-board-button secondary" onClick={handleRefreshStripe} disabled={busy}>
                        <i className="fas fa-sync-alt" aria-hidden="true"></i>
                        Tornar a consultar l&apos;estat
                    </button>
                </div>
                <p className="legal-board-muted legal-board-small">
                    Les dades s&apos;introdueixen al formulari de Stripe; Aleta no les guarda. Només Stripe confirma la verificació.
                    {stripe.checkedAt ? ` Última consulta: ${formatDateTimeCa(stripe.checkedAt)}.` : ''}
                </p>

                {actionable ? (
                    <Disclosure
                        id="legal-board-stripe-details"
                        className="legal-board-stripe-details"
                        summary={`Detall de Stripe (${requirements.length})`}
                        open={showStripeDetails}
                        onToggle={() => setShowStripeDetails((value) => !value)}
                    >
                        {statusRows}
                        {directorsDiff}
                        {stripeErrorLines(stripe).map((line) => <p key={line}>{line}</p>)}
                        <p className="legal-board-list-title">Què demana Stripe ({requirements.length})</p>
                        <ul id="legal-board-stripe-requirements" className="legal-board-list">
                            {requirements.map((label) => <li key={label}>{label}</li>)}
                        </ul>
                    </Disclosure>
                ) : null}
            </>
        );
    };

    /**
     * One step of the Stripe checklist. Titles are imperative and short; the
     * specifics (names Stripe found, the stored PDF) go in one line below.
     */
    const renderStripeTask = (task, number) => {
        let title;
        let body;
        if (task.key === 'certificate') {
            const registeredOn = formatDateCa(task.newBoardRegisteredAt);
            const needsNew = task.outdatedDocument || (!task.hasDocument && Boolean(registeredOn));
            if (needsNew || !task.hasDocument) {
                title = needsNew ? 'Demana el certificat nou al Registre' : 'Aconsegueix el certificat d’inscripció';
                body = (
                    <>
                        <p className="legal-board-stripe-task-hint">
                            {needsNew ? <>Ha de mostrar la junta inscrita el <strong>{registeredOn}</strong>. </> : null}
                            Es demana a la Generalitat: certificació de dades registrals, modalitat «Inscripció i òrgan de govern» (tràmit telemàtic amb taxa).
                            {!needsNew ? (
                                <>
                                    {' '}Si no saps com, <button type="button" className="legal-board-inline-action" onClick={onHowToKnow} disabled={busy}>mira com demanar-la</button>.
                                </>
                            ) : null}
                        </p>
                        {task.outdatedDocument ? (
                            <p className="legal-board-stripe-task-hint">
                                El que tens guardat{task.documentName ? ` (${task.documentName})` : ''} és del {formatDateCa(task.documentUploadedAt)}, d’abans de la inscripció: encara hi surt la junta anterior.
                                {editable ? (
                                    <>
                                        {' '}Quan tinguis el nou, <button type="button" className="legal-board-inline-action" onClick={() => openStep(STEP_KEYS.GENCAT)} disabled={busy}>substitueix-lo al pas 2</button>.
                                    </>
                                ) : null}
                            </p>
                        ) : null}
                        <ExternalLink link={OFFICIAL_LINKS.gencatRegistryCertificate} className="legal-board-link-button legal-board-stripe-task-action">
                            Demanar el certificat a la Generalitat
                        </ExternalLink>
                        {certificateInStripe ? (
                            <LegalBoardCertificateDocument
                                key={current.id}
                                term={current}
                                userId={userId}
                                canManage={canManage}
                                disabled={busy}
                                onReload={onReload}
                                describe={false}
                                dropHint="Quan el tinguis, guarda’l aquí per tenir-lo a mà · PDF"
                            />
                        ) : null}
                    </>
                );
            } else {
                title = 'Tingues a mà el certificat d’inscripció';
                body = (
                    <>
                        <p className="legal-board-stripe-task-hint">
                            La certificació de dades registrals de la Generalitat (òrgan de govern).
                            {` Ja la tens guardada a Aleta${task.documentName ? ` (${task.documentName})` : ''}.`}
                        </p>
                        <button type="button" className="legal-board-button link small legal-board-stripe-task-action" onClick={handleDownloadCertificate} disabled={busy}>
                            <i className="fas fa-download" aria-hidden="true"></i>
                            Descarregar el certificat guardat
                        </button>
                    </>
                );
            }
        } else if (task.key === 'directors' && task.registryOutdated) {
            title = 'Declara com a directius la junta nova';
            const missing = stripe.directorsMatch ? new Set(stripe.directorsMatch.unmatchedMembers || []) : null;
            const extra = stripe.directorsMatch?.extraDirectors || [];
            body = (
                <>
                    <p className="legal-board-stripe-task-hint">
                        Stripe encara veu al Registre la junta anterior: <strong>{task.identifiedDirectors.map((director) => director.name).join(', ')}</strong>.
                        {' '}No els afegeixis: des del {formatDateCa(task.newBoardRegisteredAt)} hi consta la junta nova.
                    </p>
                    <p className="legal-board-stripe-task-hint">Declara aquestes persones, amb el nom tal com surt al certificat nou:</p>
                    <ul className="legal-board-stripe-names" aria-label="Junta nova per declarar a Stripe">
                        {(current.members || []).map((member) => (
                            <li key={member.id || memberFullName(member)}>
                                <strong>{memberFullName(member)}</strong>
                                {member.roleLabel ? <span className="legal-board-stripe-role"> · {member.roleLabel}</span> : null}
                                {missing && !missing.has(memberFullName(member)) ? <span className="legal-board-stripe-tag">ja declarat a Stripe</span> : null}
                            </li>
                        ))}
                    </ul>
                    <NameCorrections mismatches={stripe.directorsMatch?.nameMismatches} />
                    {extra.length > 0 ? (
                        <p className="legal-board-stripe-task-hint">Treu de Stripe qui ja no és a la junta: <strong>{extra.join(', ')}</strong>.</p>
                    ) : null}
                </>
            );
        } else if (task.key === 'directors') {
            title = 'Declara com a directius les persones que surten al certificat';
            const identified = task.identifiedDirectors || [];
            body = (
                <>
                    <p className="legal-board-stripe-task-hint">Afegeix-les a Stripe amb el nom tal com consta al document: ni més ni menys.</p>
                    <NameCorrections mismatches={stripe.directorsMatch?.nameMismatches} />
                    {identified.length > 0 ? (
                        <>
                            <p className="legal-board-stripe-task-hint">Stripe ja n’ha identificat al Registre:</p>
                            <ul className="legal-board-stripe-names" aria-label="Directius identificats per Stripe">
                                {identified.map((director) => (
                                    <li key={director.name}>
                                        <strong>{director.name}</strong>
                                        {director.inBoard ? <span className="legal-board-stripe-tag">a la junta d’Aleta</span> : director.inPreviousBoard ? <span className="legal-board-stripe-tag">junta anterior a Aleta</span> : null}
                                    </li>
                                ))}
                            </ul>
                        </>
                    ) : null}
                </>
            );
        } else if (task.key === 'upload' && task.attestation) {
            title = 'Signa electrònicament la llista de directius';
            body = (
                <p className="legal-board-stripe-task-hint">
                    A «Completar-ho a Stripe», comprova que els directius són els del certificat.
                    {' '}Al final, tria «Signar electrònicament» (no «Aportar documentació») i escriu el nom de la presidència.
                </p>
            );
        } else if (task.key === 'upload') {
            title = task.withSigner ? `Puja el certificat${task.newCertificate ? ' nou' : ''} i indica qui el signa` : `Puja el certificat${task.newCertificate ? ' nou' : ''} a Stripe`;
            body = (
                <p className="legal-board-stripe-task-hint">
                    Adjunta el PDF com a prova d’inscripció (anvers i revers, si els demana per separat).
                    {task.withSigner ? ' Quan pregunti qui l’ha signat, tria una de les persones declarades al compte.' : ''}
                </p>
            );
        } else {
            title = 'Completa la resta de dades';
            body = <p className="legal-board-stripe-task-hint">{(task.labels || []).join(' · ')}</p>;
        }
        return (
            <li key={task.key} className="legal-board-stripe-task">
                <span className="legal-board-stripe-task-number" aria-hidden="true">{number}</span>
                <div className="legal-board-stripe-task-body">
                    <p className="legal-board-stripe-task-title">{title}</p>
                    {body}
                </div>
            </li>
        );
    };

    const bodies = {
        [STEP_KEYS.BOARD]: renderBoardBody,
        [STEP_KEYS.GENCAT]: renderGencatBody,
        [STEP_KEYS.STRIPE]: renderStripeBody,
    };

    const renderStep = (key, index) => {
        const step = steps[key];
        const open = isOpen(key);
        return (
            <li key={key} className={`legal-board-flow-item ${step.state}`}>
                <button
                    type="button"
                    className="legal-board-flow-head"
                    aria-expanded={open}
                    aria-controls={`legal-board-flow-${key}`}
                    onClick={() => toggle(key)}
                >
                    <span className={`legal-board-flow-icon ${step.state}`} aria-hidden="true">
                        {step.state === 'done' ? <i className="fas fa-check"></i> : step.state === 'na' ? '–' : index + 1}
                    </span>
                    <span className="legal-board-sr-only">{STATE_LABELS[step.state]}. </span>
                    <span className="legal-board-flow-title">{STEP_TITLES[key]}</span>
                    <span className="legal-board-flow-summary">{step.summary}</span>
                    <i className={`fas fa-chevron-${open ? 'up' : 'down'} legal-board-flow-chevron`} aria-hidden="true"></i>
                </button>
                {open ? (
                    <div id={`legal-board-flow-${key}`} className="legal-board-flow-body">
                        {bodies[key]()}
                    </div>
                ) : null}
            </li>
        );
    };

    const totalEvents = history.reduce((sum, term) => sum + (term.events || []).length, 0);
    const historySummary = `Historial · ${history.length} ${history.length === 1 ? 'mandat' : 'mandats'} · ${totalEvents} ${totalEvents === 1 ? 'actuació' : 'actuacions'}`;

    return (
        <section className="legal-board-card legal-board-flow-card" aria-labelledby="legal-board-title">
            <header className="legal-board-card-head">
                <h2 id="legal-board-title">Registre d&apos;Associacions</h2>
                <StatusPill tone={summary.pill.tone}>{summary.pill.label}</StatusPill>
            </header>
            <div className="legal-board-summary">
                <p className={summary.renew ? 'legal-board-summary-alert' : 'legal-board-muted'}>{summary.line}</p>
                {summary.renew && canManage ? (
                    <button type="button" className={`legal-board-button ${activeKey ? 'link' : 'primary'}`} onClick={onPrepareChange} disabled={busy}>
                        Preparar un canvi de junta
                    </button>
                ) : null}
            </div>
            {error ? <div className="legal-board-alert error" role="alert">{error}</div> : null}

            <ol className="legal-board-flow" aria-label="Passos">
                {STEP_ORDER.map((key, index) => renderStep(key, index))}
            </ol>

            {history.length > 0 ? (
                <Disclosure id="legal-board-history" className="legal-board-history-disclosure" summary={historySummary} open={showHistory} onToggle={() => setShowHistory((value) => !value)}>
                    <LegalBoardHistory history={history} userId={userId} embedded />
                </Disclosure>
            ) : null}
        </section>
    );
}

export default LegalBoardSteps;
